import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Rating from './Rating';
import { FaThumbsUp, FaThumbsDown, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import Avatar from './Avatar';
import Spinner from './Spinner';
import { useLanguage } from '../contexts/LanguageContext';

export interface ServerReview {
  id: string;
  rating: number;
  content: string;
  createdAt: string;
  updatedAt: string;
  helpfulCount: number;
  notHelpfulCount: number;
  user?: {
    id: string;
    username: string;
    displayName?: string | null;
    avatarUrl?: string | null;
  };
}

interface ServerReviewsProps {
  reviews: ServerReview[];
  className?: string;
  isLoading?: boolean;
  errorMessage?: string | null;
  onVote?: (reviewId: string, vote: 'helpful' | 'not_helpful') => void;
  pendingVoteIds?: Set<string>;
  currentUserId?: string | null;
}

const formatReviewDate = (value: string) => {
  try {
    return new Date(value).toLocaleDateString();
  } catch (error) {
    return value;
  }
};

const VISIBLE_REVIEWS = 3;

const ServerReviews: React.FC<ServerReviewsProps> = ({
  reviews,
  className = '',
  isLoading = false,
  errorMessage = null,
  onVote,
  pendingVoteIds,
  currentUserId,
}) => {
  const { t } = useLanguage();
  const [startIndex, setStartIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const isVotingDisabled = (review: ServerReview) => {
    if (!onVote) {
      return true;
    }
    if (pendingVoteIds?.has(review.id)) {
      return true;
    }

    if (review.user?.id && currentUserId && review.user.id === currentUserId) {
      return true;
    }

    return false;
  };

  const canScrollLeft = startIndex > 0;
  const canScrollRight = startIndex + VISIBLE_REVIEWS < reviews.length;

  const handleScrollLeft = () => {
    setStartIndex(Math.max(0, startIndex - 1));
  };

  const handleScrollRight = () => {
    setStartIndex(Math.min(reviews.length - VISIBLE_REVIEWS, startIndex + 1));
  };

  const visibleReviews = reviews.slice(startIndex, startIndex + VISIBLE_REVIEWS);
  const showNavigation = reviews.length > VISIBLE_REVIEWS;

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white">{t('server.reviews.title') || 'Player Reviews'}</h3>

        {/* Navigation Arrows */}
        {showNavigation && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleScrollLeft}
              disabled={!canScrollLeft}
              className={`p-2 rounded-full transition-colors ${canScrollLeft
                ? 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-white'
                : 'bg-gray-100 dark:bg-gray-700/50 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                }`}
              aria-label="Previous reviews"
            >
              <FaChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {startIndex + 1}-{Math.min(startIndex + VISIBLE_REVIEWS, reviews.length)} / {reviews.length}
            </span>
            <button
              onClick={handleScrollRight}
              disabled={!canScrollRight}
              className={`p-2 rounded-full transition-colors ${canScrollRight
                ? 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-white'
                : 'bg-gray-100 dark:bg-gray-700/50 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                }`}
              aria-label="Next reviews"
            >
              <FaChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {errorMessage && (
        <div className="mb-4 rounded-md border border-yellow-300 dark:border-yellow-600 bg-yellow-50 dark:bg-yellow-900/40 px-4 py-3 text-sm text-yellow-700 dark:text-yellow-100">
          {errorMessage}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
          <Spinner className="text-blue-400" size="sm" />
          <span>{t('server.reviews.loading') || 'Loading reviews...'}</span>
        </div>
      ) : reviews.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400">{t('server.reviews.no_reviews') || 'No reviews yet. Be the first to share your experience.'}</p>
      ) : (
        <div ref={scrollContainerRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleReviews.map((review) => {
            const displayName = review.user?.displayName || review.user?.username || (t('server.reviews.anonymous') || 'Anonymous Player');
            const reviewDate = formatReviewDate(review.createdAt);
            const disableVoting = isVotingDisabled(review);

            return (
              <div key={review.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 flex flex-col h-full border border-gray-200 dark:border-gray-600/50 hover:border-gray-300 dark:hover:border-gray-500 transition-colors">
                <div className="flex items-start gap-3 mb-3">
                  <Avatar src={review.user?.avatarUrl ?? undefined} alt={displayName} size="md" />
                  <div className="flex-1 min-w-0">
                    {review.user?.id ? (
                      <Link
                        to={`/user/${review.user.id}`}
                        className="font-bold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors truncate block"
                      >
                        {displayName}
                      </Link>
                    ) : (
                      <h4 className="font-bold text-gray-900 dark:text-white truncate">{displayName}</h4>
                    )}
                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mt-1">
                      <Rating rating={review.rating} size="sm" />
                      <span className="text-xs">{reviewDate}</span>
                    </div>
                  </div>
                </div>

                <p className="text-gray-700 dark:text-gray-300 text-sm flex-1 line-clamp-4 mb-3">{review.content}</p>

                {onVote && (
                  <div className="flex items-center gap-2 text-sm mt-auto pt-3 border-t border-gray-200 dark:border-gray-600/50">
                    <button
                      type="button"
                      onClick={() => onVote(review.id, 'helpful')}
                      disabled={disableVoting}
                      className={`flex items-center gap-1 rounded-md border px-2 py-1 transition text-xs ${disableVoting
                        ? 'cursor-not-allowed border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-600/50 text-gray-400 dark:text-gray-500'
                        : 'border-blue-400 dark:border-blue-600 text-blue-600 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-600/20'
                        }`}
                      title={t('server.reviews.helpful') || 'Helpful'}
                    >
                      <FaThumbsUp className={review.helpfulCount > 0 ? "text-blue-500 dark:text-blue-400" : ""} />
                      <span>{review.helpfulCount}</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => onVote(review.id, 'not_helpful')}
                      disabled={disableVoting}
                      className={`flex items-center gap-1 rounded-md border px-2 py-1 transition text-xs ${disableVoting
                        ? 'cursor-not-allowed border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-600/50 text-gray-400 dark:text-gray-500'
                        : 'border-red-400 dark:border-red-600 text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-600/20'
                        }`}
                      title={t('server.reviews.not_helpful') || 'Not helpful'}
                    >
                      <FaThumbsDown className={review.notHelpfulCount > 0 ? "text-red-500 dark:text-red-400" : ""} />
                      <span>{review.notHelpfulCount}</span>
                    </button>

                    {disableVoting && review.user?.id === currentUserId && (
                      <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto">{t('server.reviews.own_review') || 'Sizin yorumunuz'}</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ServerReviews;
